import { AnimatePresence, motion } from 'motion/react';

import { cn } from '@april9au/react-common/utils';

import type { JourneyModule, JourneyStep } from '@/lib/topic-state';
import { formatScore } from '@/chat/scoring';
import { MotionLink } from '@/components/motion/motion-ui';
import { pressSpring } from '@/components/motion/tokens';
import { pressProps, trailStopVariants, trailTickVariants } from '@/components/motion/variants';
import { Stars } from '@/components/stars';
import { Icon } from '@/components/ui/icon';
import { Media } from '@/components/ui/media';
import { useRootLoaderData } from '@/hooks/use-root-loader-data';
import { formatString } from '@/i18n/catalogue';
import { useStrings } from '@/i18n/use-strings';

/**
 * One stop on the trail: a rail node, and a card that is entirely one tap
 * target.
 *
 * The three states differ by **light, not by material** — every card is the
 * same white frost, and the recommended one is the one the ground is lit
 * behind. That is deliberate: a "locked" grey card would imply the module is
 * unavailable, and it is not. A `todo` module is always openable; the state
 * only decides emphasis.
 *
 * The recommended module carries no button of its own. The screen's yellow
 * primary action lives once, in the bottom bar where a thumb already is
 * (`ActionBar`), so there is never a second thing competing to be the obvious
 * next tap. The card names itself "Up next" and the halo does the rest.
 *
 * Most production topics have neither `description` nor `duration_minutes`,
 * so the card's rhythm is built from the icon, the label and the title alone
 * and the optional rows simply extend it — nothing reserves empty space.
 *
 * State is never carried by colour alone: `done` shows a tick and its stars,
 * `next` names itself in a chip, and every card states its state in text.
 */
export function ModuleCard({
  step,
  href,
  /** Best score, 1–5, for a finished module. */
  score,
  /** The rail stops at the last card rather than running off the trail. */
  isLast,
}: {
  step: JourneyStep<JourneyModule>;
  href: string;
  score: number | undefined;
  isLast: boolean;
}) {
  const { journey } = useStrings();
  const { language } = useRootLoaderData();
  const { state, progress } = step;

  // A started module says so instead of naming its position in the queue:
  // "In progress" is what the learner needs to see to know where they were,
  // and the bar below says how far. `next` keeps its yellow chip either way —
  // the chip is "where you are", which a resumable module still is.
  const stateLabel = progress
    ? journey.journey_module_in_progress
    : state === 'done'
      ? journey.journey_module_done
      : state === 'next'
        ? journey.journey_module_next
        : journey.journey_module_todo;

  const scoreLabel =
    score === undefined ? '' : formatString(journey.journey_module_score, { score: formatScore(score, language) });

  const percent = progress ? Math.round(progress.fraction * 100) : 0;
  // A finished module is full whether or not a run survived for it — the score
  // is what records that it was completed.
  const fill = state === 'done' ? 1 : (progress?.fraction ?? 0);

  return (
    <motion.li variants={trailStopVariants} className="relative ps-12">
      {/* The rail: a node per module, joined down to the next one. Yellow
          behind the learner, glass ahead of them. */}
      {isLast ? null : (
        <span
          aria-hidden
          className={cn(
            // `-bottom-4` bridges the list's gap, so the trail reads as one
            // continuous line rather than four dashes.
            'absolute start-[0.9375rem] top-12 -bottom-4 w-0.5 rounded-full transition-colors duration-300 motion-reduce:transition-none',
            state === 'done' ? 'bg-[var(--journey-rail-done)]' : 'bg-[var(--journey-rail-todo)]',
          )}
        />
      )}

      <span
        aria-hidden
        className={cn(
          'absolute start-0 top-4 grid size-8 place-items-center rounded-full text-xs font-bold tabular-nums',
          'transition-colors duration-300 motion-reduce:transition-none',
          state === 'done' && 'bg-surf-yellow text-surf-dark-blue',
          state === 'next' && 'ring-surf-yellow text-surf-dark-blue bg-white ring-[3px]',
          state === 'todo' && 'bg-white/15 text-white/70 ring-1 ring-white/25',
        )}
      >
        <AnimatePresence initial={false} mode="wait">
          {state === 'done' ? (
            <motion.span key="tick" variants={trailTickVariants} initial="hidden" animate="shown" exit="hidden">
              <Icon name="passport/check" className="size-4" />
            </motion.span>
          ) : (
            <motion.span key="n" variants={trailTickVariants} initial="hidden" animate="shown" exit="hidden">
              {step.position}
            </motion.span>
          )}
        </AnimatePresence>
      </span>

      <MotionLink
        to={href}
        viewTransition
        {...pressProps}
        transition={pressSpring}
        className={cn(
          'bp-tappable text-surf-dark-blue block rounded-3xl p-4 outline-none sm:p-5',
          'transition-[background-color,border-color,box-shadow] duration-300 motion-reduce:transition-none',
          'focus-visible:shadow-[var(--bp-focus-ring)]',
          state === 'done' ? 'journey-card-done' : 'journey-card',
          state === 'next' && 'journey-card-next',
        )}
      >
        <div className="flex items-start gap-3.5">
          {/* Fixed box whether or not the topic has an icon, so nothing
              shifts when Prismic's artwork lands. */}
          <span
            className={cn(
              'grid size-12 shrink-0 place-items-center overflow-hidden rounded-2xl',
              state === 'done' ? 'bg-surf-blue/[0.06]' : 'bg-surf-blue/10',
            )}
          >
            {step.iconUrl ? (
              <Media
                src={step.iconUrl}
                alt={step.iconAlt}
                width={88}
                height={88}
                placeholder="transparent"
                className="size-9 object-contain"
              />
            ) : (
              <Icon name="passport/modules" aria-hidden className="text-surf-blue size-5" />
            )}
          </span>

          <div className="min-w-0 flex-1">
            <p
              dir="auto"
              className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.6875rem] font-bold tracking-[0.08em] uppercase"
            >
              {/* `dir="auto"`: an English fallback inside an RTL page
                  otherwise reorders into `1 Module`. */}
              {/* `--bp-ink-muted` rather than a tinted blue: it is the ticket-14
                  token measured for secondary ink on a frosted card. */}
              <span className="text-bp-ink-muted">
                {formatString(journey.journey_module_label, { position: step.position })}
              </span>

              {state === 'next' ? (
                <span className="bg-surf-yellow text-bp-on-yellow rounded-full px-2 py-0.5 tracking-[0.06em]">
                  {stateLabel}
                </span>
              ) : (
                <span className={cn(state === 'done' ? 'text-surf-teal' : 'text-bp-ink-muted')}>{stateLabel}</span>
              )}
            </p>

            {/* A finished module is quieted by its *surface*, never by
                weakening its title: navy at 70 % over a translucent white card
                sitting on a bright photograph does not clear 4.5:1. */}
            <h2
              dir="auto"
              className="font-bebas mt-1 text-[1.375rem] leading-[1.05] tracking-[0.015em] text-pretty uppercase sm:text-2xl"
            >
              {step.title}
            </h2>

            {step.description ? (
              <p dir="auto" className="text-bp-ink-muted mt-1.5 text-sm leading-snug text-pretty">
                {step.description}
              </p>
            ) : null}

            {/* Every card carries the same track, so the trail reads as one
                gauge down the page rather than as bars appearing and
                disappearing: empty grey before the module is opened, yellow to
                the steps played, and full navy once it is done. The track and
                its fill are the module header's rail at card scale, so "how far
                in" looks the same wherever the learner meets it.

                Decorative — the state is already in text above ("To do", "In
                progress", "Done") and the percentage below is real text, so the
                bar adds nothing to the reading order. */}
            <div className="mt-2.5 flex items-center gap-2">
              <div aria-hidden className="bg-surf-blue/12 relative h-1.5 min-w-0 flex-1 overflow-hidden rounded-full">
                <span
                  className={cn(
                    'absolute inset-0 origin-left rounded-full rtl:origin-right',
                    state === 'done' ? 'bg-surf-dark-blue' : 'bg-surf-yellow',
                  )}
                  style={{ transform: `scaleX(${fill})` }}
                />
              </div>

              {/* Only a part-finished module has a number worth printing: "0%"
                  is noise on an untouched card, and a finished one says it with
                  its tick and stars. */}
              {progress ? (
                <span dir="auto" className="text-bp-ink-muted shrink-0 text-xs font-semibold tabular-nums">
                  {formatString(journey.journey_module_progress, { percent })}
                </span>
              ) : null}
            </div>

            {/* The optional row. Absent on almost every production topic, and
                absent means absent — no placeholder, no reserved height. */}
            {step.durationMinutes === null && score === undefined ? null : (
              <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                {step.durationMinutes === null ? null : (
                  <span
                    dir="auto"
                    className="text-bp-ink-muted inline-flex items-center gap-1 text-xs font-semibold tabular-nums"
                  >
                    <Icon name="passport/clock" aria-hidden className="size-3.5" />
                    {formatString(journey.journey_module_duration, { minutes: step.durationMinutes })}
                  </span>
                )}

                {score === undefined ? null : (
                  <span className="inline-flex text-sm" title={scoreLabel}>
                    <Stars score={score} />
                    <span className="sr-only">{scoreLabel}</span>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* The affordance. Quiet on purpose: it says "this opens", not
              "press me" — the bottom bar says that. */}
          <Icon
            name="passport/chevron-right"
            aria-hidden
            className={cn(
              'mt-3 size-5 shrink-0 rtl:rotate-180',
              state === 'done' ? 'text-surf-blue/35' : 'text-surf-blue/70',
            )}
          />
        </div>
      </MotionLink>
    </motion.li>
  );
}
